import { compact, size } from 'lodash-es'
import { Question as QuestionAPI } from 'types/glass-api/domainModels'
import { Concept, getConcepts } from './concepts'
import {
  buildConceptDisplayLogic,
  buildQuestionDisplayLogic,
  DisplayLogic,
  DisplayLogicByResource,
} from './displayLogic'
import { buildPipeConceptFeature, PipeConceptFeature } from './piping'
import { buildTestLabel, hasFeature } from './questions'
import { getRequireViewConceptError } from './requireViewing'
import { sanitizeHtml } from './richText'
import { isScaleOption } from './scale'

export interface Option {
  id: string
  labels: string[]
  title: string
  values: number[]
}

export type Question = {
  concepts: Concept[]
  constraints: {
    requireConceptView: boolean
  }
  directions: string
  features: {
    conceptDisplayLogic: DisplayLogicByResource
    displayLogic: DisplayLogic
    pipeConcept: PipeConceptFeature | undefined
  }
  id: string
  labels: string[]
  options: Option[]
  response: Record<string, number>
  testing: { label: string }
  title: string
  type: 'bipolar'
}

export function canContinue({
  curResponse,
  question,
}: {
  curResponse: Question['response']
  question: Question
}) {
  return (
    size(curResponse) === question.options.length &&
    !getRequireViewConceptError({ question })
  )
}

export function getQuestion({
  id,
  question,
}: {
  id: string
  question: QuestionAPI
}) {
  return {
    concepts: getConcepts({ question }),
    constraints: {
      requireConceptView: hasFeature({ code: 'VAL02', question }),
    },
    directions: sanitizeHtml(question.description),
    features: {
      conceptDisplayLogic: buildConceptDisplayLogic({ question }),
      displayLogic: buildQuestionDisplayLogic({ question }),
      pipeConcept: buildPipeConceptFeature({ question }),
    },
    id,
    labels: [
      'Agree with A much more',
      'Agree with A somewhat more',
      'Agree with B somewhat more',
      'Agree with B much more',
    ],
    options: compact(
      question.options.map((option, i) => {
        // The currently configured bipolar questions are configured as scales.
        // Scales allowed the closest configuration to what a bipolar question should be.
        if (!isScaleOption(option) || option.step <= 0) {
          return
        }

        const { high, highLabel, low, lowLabel, step } = option
        const values: Question['options'][number]['values'] = []

        for (let i = low; i <= high; i += step) {
          values.push(i)
        }

        return {
          id: `${option.id}`,
          labels: [lowLabel, highLabel] as const,
          title: `Option ${i + 1}`,
          values,
        }
      }),
    ),
    response: {},
    testing: { label: buildTestLabel(question) },
    title: sanitizeHtml(question.title),
    type: 'bipolar' as const,
  } satisfies Question
}

export function isBipolarDisabled({ question }: { question: Question }) {
  if (
    question.constraints.requireConceptView &&
    question.concepts.length > 0 &&
    !question.concepts[0].viewed
  ) {
    return true
  }

  return false
}
